import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import Icon from '~/components/data-display/Icon';
import SimpleTooltip from '~/components/data-display/SimpleTooltip';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { useShortDocumentLabel } from '~/hooks/documents/useDocumentLabel';
import IconArrowTrendDown from '~/icons/icon-arrow-trend-down.svg?react';
import IconArrowTrendUp from '~/icons/icon-arrow-trend-up.svg?react';
import IconBulb from '~/icons/icon-bulb.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import DocumentIcon from '~/icons/icon-document.svg?react';
import PlusIcon from '~/icons/icon-plus.svg?react';
import { useDocumentQuery } from '~/services/document';
import { DocumentType, Document } from '~/types/document';
import { counterpartName } from '~/utils/counterparts';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import { formatMoney } from '~/utils/money';
import styles from './BookkeepingTransactionRow.module.scss';

interface Props {
  documentId: string | null;
  documentNumber: string | null;
  documentType: DocumentType | null;
  readOnly?: boolean;
  onRemoveDocumentClick: () => void;
  onDocumentClick: () => void;
  isARecommendedReconciliation: boolean;
}

const TooltipContent = ({
  document,
  isARecommendedReconciliation,
}: {
  document: Document;
  isARecommendedReconciliation: boolean;
}) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.create.form' });
  const { t: documentsT } = useTranslation('documents');
  return (
    <VStack gap={2} className={styles.tooltip}>
      {isARecommendedReconciliation && (
        <HStack className={styles.recommendedReconciliation} gap={1} align="center">
          <div className={styles.icon}>
            <Icon component={IconBulb} size="xs" />
          </div>
          <Text className={styles.text} variant="body2" weight="medium">
            {t('tooltip.recommended_reconciliation')}
          </Text>
        </HStack>
      )}
      <HStack gap={1} align="start">
        <Text variant="body1" weight="semibold">
          {counterpartName(document.counterpart)}
        </Text>
        <HStack gap={0.5}>
          <Text variant="body1" weight="semibold">
            {formatMoney(document.grossAmount)}
          </Text>
          {document.isInflow ? (
            <Icon component={IconArrowTrendUp} className={styles.trendUpIcon} />
          ) : (
            <Icon component={IconArrowTrendDown} className={styles.trendDownIcon} />
          )}
        </HStack>
      </HStack>
      <Text variant="body2" weight="regular" secondaryColor>
        {t('tooltip.document_info', {
          type: documentsT(`type.${document.documentType}`),
          number: document.number,
          date: formatFromISOToDisplay(document.creationDate),
        })}
      </Text>
    </VStack>
  );
};

const AssociatedDocumentCell: React.FC<Props> = ({
  documentId,
  documentNumber,
  documentType,
  readOnly,
  onRemoveDocumentClick,
  onDocumentClick,
  isARecommendedReconciliation,
}) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.create.form' });
  const shortDocumentLabel = useShortDocumentLabel();
  const { data: document } = useDocumentQuery(documentId ?? undefined);

  return (
    <>
      {documentId && document ? (
        <SimpleTooltip
          className={styles.tooltipControl}
          theme="light"
          title={<TooltipContent document={document} isARecommendedReconciliation={isARecommendedReconciliation} />}
        >
          <HStack gap={1} align="center" justify="space-between" className={styles.documentSelection}>
            <HStack gap={1} align="center" className={styles.selectedDocument}>
              <Icon component={DocumentIcon} size="sm" />
              {documentType && (
                <Text variant="body2" weight="semibold">
                  {shortDocumentLabel(documentType, documentNumber ?? '')}
                </Text>
              )}
            </HStack>
            {!readOnly && (
              <IconButton
                variant="ghost"
                size="small"
                Icon={IconClose}
                onClick={onRemoveDocumentClick}
                aria-label={t('description')}
              />
            )}
          </HStack>
        </SimpleTooltip>
      ) : (
        <HStack gap={1} align="center" justify="space-between" className={styles.documentSelection}>
          {!readOnly && (
            <>
              <Text variant="body2" weight="regular" secondaryColor>
                {t('no_document')}
              </Text>
              <IconButton
                variant="ghost"
                Icon={PlusIcon}
                size="small"
                onClick={onDocumentClick}
                aria-label={t('description')}
              />
            </>
          )}
        </HStack>
      )}
    </>
  );
};

export default AssociatedDocumentCell;
